import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export const SplitDropdownMenu = ({ anchorRef, items, visible, onClose }) => {
    const menuRef = useRef(null);
    const [position, setPosition] = useState({ top: 0, left: 0 });

    // --- Calculate dropdown position ---
    const updatePosition = () => {
        if (anchorRef.current && menuRef.current) {
            const rect = anchorRef.current.getBoundingClientRect();
            const dropdownWidth = 180; // adjust to your menu width
            const dropdownHeight = menuRef.current.offsetHeight;
            const viewportHeight = window.innerHeight;
            const viewportWidth = window.innerWidth;

            let top = rect.bottom;
            let left = rect.left;

            const spaceBelow = viewportHeight - rect.bottom;
            const spaceAbove = rect.top;

            if (dropdownHeight > viewportHeight) {
                // Dropdown taller than viewport → center in screen
                top = (viewportHeight - dropdownHeight) / 2;
            } else if (spaceBelow >= dropdownHeight) {
                // Fits below → keep default
                top = rect.bottom;
            } else if (spaceAbove >= dropdownHeight) {
                // Fits above → show above
                top = rect.top - dropdownHeight;
            } else {
                // Doesn’t fully fit above or below → center around anchor
                const anchorMiddle = rect.top + rect.height / 2;
                top = Math.max(0, anchorMiddle - dropdownHeight / 2);
                if (top + dropdownHeight > viewportHeight) {
                    top = viewportHeight - dropdownHeight; // clamp inside screen
                }
            }

            // Horizontal overflow check
            if (left + dropdownWidth > viewportWidth) {
                left = viewportWidth - dropdownWidth - 110;
            }

            setPosition({ top, left });
        }
    };


    // --- Update on open/scroll/resize ---
    useEffect(() => {
        if (visible) {
            updatePosition();
            window.addEventListener("scroll", updatePosition, true);
            window.addEventListener("resize", updatePosition);
        }
        return () => {
            window.removeEventListener("scroll", updatePosition, true);
            window.removeEventListener("resize", updatePosition);
        };
    }, [visible]);

    // --- Close on outside click ---
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (
                menuRef.current &&
                !menuRef.current.contains(e.target) &&
                !anchorRef.current.contains(e.target)
            ) {
                onClose();
            }
        };
        if (visible) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [visible, onClose]);

    if (!visible) return null;

    // Honeyed sectioned menu (.claude/pp-dialogs-prototype.html's .menu):
    // items can be a section label ({type:'label', text}), a divider
    // ({type:'sep'}), or an action — visible:false items are dropped
    // entirely (not rendered greyed-out), disabled ones render but don't
    // fire their command.
    const visibleItems = items.filter((item) => item.visible !== false);

    return createPortal(
        <div
            ref={menuRef}
            className="pp-menu"
            style={{
                position: "fixed",
                top: position.top,
                left: position.left,
                zIndex: 1000,
            }}
        >
            {visibleItems.map((item, idx) => {
                if (item.type === 'label') return <div className="lbl" key={idx}>{item.text}</div>;
                if (item.type === 'sep') return <div className="sep" key={idx}></div>;

                return (
                    <div
                        key={idx}
                        role="menuitem"
                        tabIndex={item.disabled ? -1 : 0}
                        aria-disabled={!!item.disabled}
                        className={`item ${item.danger ? 'danger' : ''} ${item.disabled ? 'disabled' : ''}`}
                        onClick={() => {
                            if (item.disabled) return;
                            if (item.command) item.command();
                            onClose();
                        }}
                    >
                        {item.icon}
                        <div>{item.label}</div>
                    </div>
                );
            })}
        </div>,
        document.getElementById('portal-root') || document.body
    );
};
